import {Component, EventEmitter, HostListener, Input, OnInit, Output, TemplateRef} from '@angular/core';
import {DocumentsApiService} from '../../../services/api/documents-api.service';
import {
    ColonsModel,
    DocsAccess,
    NavigationTableResponseItemModel,
    PermissionsDocsModel
} from '../../../models/file-table.model';
import {AlertService} from '../../../services/notify/alert.service';
import {HelpersService} from '../../../services/helpers.service';
import {PermissionsBoardModel} from '../../../models/tasktable.model';
import {MemberModel} from '../../../models/user.model';
import {DepartmentModel, StructureDepartmentModel} from '../../../models/department.model';
import {ModalService} from '../../../services/modal/modal.service';
import {DepartmentsService} from '../../../services/api/departments.service';
import {MemberService} from '../../../services/member.service';
import {NgbModalRef} from '@ng-bootstrap/ng-bootstrap/modal/modal-ref';
import {Observable} from 'rxjs';
import {ComponentCanDeactivate} from '../../../guards/save-guard';
import {FileModel} from '../../../models/file.model';
import {TranslateService} from '@ngx-translate/core';

interface PermissionRequestModel extends DocsAccess {
    role_type: 'member' | 'department' | 'division';
    role_key: string | number;
}

@Component({
    selector: 'app-documents-access-settings',
    templateUrl: './documents-access-settings.component.html',
    styleUrls: ['./documents-access-settings.component.scss']
})
export class DocumentsAccessSettingsComponent implements OnInit, ComponentCanDeactivate {
    @Input() target: NavigationTableResponseItemModel | FileModel;
    @Output() changeEditEvent: EventEmitter<boolean> = new EventEmitter<boolean>();
    colons: ColonsModel[];
    departments: PermissionsDocsModel[] = [];
    divisions: PermissionsDocsModel[] = [];
    members: PermissionsDocsModel[] = [];
    isPermissionsChanged = false;
    copyPermissions: {
        departments: PermissionsDocsModel[],
        divisions: PermissionsDocsModel[],
        members: PermissionsDocsModel[],
    } = {
        departments: [],
        divisions: [],
        members: [],
    };
    allDepartments: StructureDepartmentModel[] = [];
    selectedDepartmentWithDivisions: StructureDepartmentModel;
    divisionSlider: {
        [key: number]: boolean
    } = {};
    allMembers: MemberModel[] = [];
    isLockButton = false;
    roleType: 'member' | 'department' | 'division';
    roleKey: string | number;
    editedPermissions: {
        [key: string]: PermissionRequestModel
    } = {};

    constructor(
        private docApi: DocumentsApiService,
        private alert: AlertService,
        public helper: HelpersService,
        private modalService: ModalService,
        private departmentsApi: DepartmentsService,
        private memberService: MemberService,
        public translate: TranslateService
    ) {
    }

    @HostListener('window: beforeunload', ['$event'])
    deactivate(): boolean {
        if (this.isPermissionsChanged) {
            return false;
        }
    }

    canDeactivate(): boolean | Observable<boolean> {
        if (this.isPermissionsChanged) {
            return confirm(this.translate.instant('CONFIRM.TEXT.LEAVE_PAGE'));
        } else {
            return true;
        }
    }

    ngOnInit(): void {
        this.getAccess();
    }

    getAccess(): void {
        const rightKey = this.target.key ? this.target.key : (this.target as NavigationTableResponseItemModel).id;
        this.docApi.getDocAccess(this.target.type_str, rightKey).subscribe({
            next: res => {
                this.colons = res.structure;
                this.departments = res.departments;
                this.divisions = res.divisions;
                this.members = res.members;

                this.copyPermissions.departments = structuredClone(res.departments);
                this.copyPermissions.divisions = structuredClone(res.divisions);
                this.copyPermissions.members = structuredClone(res.members);
            }, error: err => {
                this.alert.setAlert(err.message, 'blue');
            }
        });
    }

    async openCreateModal(roleType: 'division' | 'department' | 'member', content: TemplateRef<any>): Promise<void> {
        this.roleType = roleType;
        if (roleType === 'member') {
            await this.getAllUsers();
        } else {
            await this.getAllDepartments();
        }
        this.modalService.openTemplateModal(content);
    }

    async getAllDepartments(): Promise<void> {
        return new Promise(resolve => {
            this.departmentsApi.getStructureDepartments().subscribe({
                next: res => {
                    this.allDepartments = res.departments;
                    resolve();
                }
            });
        });
    }

    async getAllUsers(): Promise<void> {
        return new Promise(resolve => {
            this.memberService.getAllMembers().then(res => {
                this.allMembers = res.filter(x => x.project_setting.observation_type !== 'blocked' && !x.project_setting.is_project_admin);
                resolve();
            });
        });
    }

    addPermissionsToBoard(readAll: string | 'succession' | 'allowed' | 'denied',
                          uploadFile: string | 'succession' | 'allowed' | 'denied',
                          createFolder: string | 'succession' | 'allowed' | 'denied',
                          editSigner: string | 'succession' | 'allowed' | 'denied',
                          lockedCollection: string | 'succession' | 'allowed' | 'denied',
                          editSettings: string | 'succession' | 'allowed' | 'denied',
                          isDelete: string | 'succession' | 'allowed' | 'denied',
                          readApprovedDoc: string | 'succession' | 'allowed' | 'denied',
                          editApprover: string | 'succession' | 'allowed' | 'denied',
                          editProperty: string | 'succession' | 'allowed' | 'denied',
                          modal: NgbModalRef
    ): void {
        const data = {
            role_type: this.roleType,
            role_key: this.roleKey,
            is_create_folder: createFolder,
            is_edit_settings: editSettings,
            is_edit_signer: editSigner,
            is_locked_collection: lockedCollection,
            is_read_all: readAll,
            is_delete: isDelete,
            is_upload_file: uploadFile,
            is_read_approved_doc: readApprovedDoc,
            is_edit_approver: editApprover,
            is_edit_property: editProperty,
        };

        const rightKey = this.target.key ? this.target.key : (this.target as NavigationTableResponseItemModel).id;

        this.docApi.setDocAccess(this.target.type_str, rightKey, data).subscribe({
            next: () => {
                this.getAccess();
                this.alert.setAlert(this.translate.instant('ALERTS.SUCCESS'), 'green');
                this.selectedDepartmentWithDivisions = undefined;
                this.roleKey = undefined;
                modal.close();
            }, error: err => {
                this.alert.setAlert(err.message, 'blue');
            }
        });
    }

    changePermissions(
        permission: PermissionsDocsModel,
        key: string,
        value: string | 'succession' | 'allowed' | 'denied'
    ): void {
        if (this.colons.find(x => !x.is_editing).right === key) {
            return;
        }

        this.isPermissionsChanged = true;
        this.changeEditEvent.emit(this.isPermissionsChanged);

        const editingPermission = permission;
        const currentRoleKey = editingPermission.role_type === 'member' ?
            (editingPermission.role as MemberModel).guid : (editingPermission.role as DepartmentModel).id;
        editingPermission[key] = value;

        if (!this.editedPermissions[currentRoleKey]) {
            const data = {
                role_type: editingPermission.role_type,
                role_key: currentRoleKey,
                is_create_folder: editingPermission.is_create_folder,
                is_edit_settings: editingPermission.is_edit_settings,
                is_locked_collection: editingPermission.is_locked_collection,
                is_read_all: editingPermission.is_read_all,
                is_upload_file: editingPermission.is_upload_file,
                is_read_approved_doc: editingPermission.is_read_approved_doc,
                is_edit_signer: editingPermission.is_edit_signer,
                is_edit_approver: editingPermission.is_edit_approver,
                is_edit_property: editingPermission.is_edit_property,
                is_delete: editingPermission.is_delete,
            };
            this.editedPermissions[currentRoleKey] = structuredClone(data);
        } else {
            const checkChanges: PermissionsDocsModel = this.copyPermissions[editingPermission.role_type + 's']
                .find((copyPermission: PermissionsBoardModel) => {
                    const permissionKey = copyPermission.role_type === 'member' ?
                        (copyPermission.role as MemberModel).guid : (copyPermission.role as DepartmentModel).id;
                    if (permissionKey === currentRoleKey) {
                        return copyPermission;
                    }
                });

            this.editedPermissions[currentRoleKey][key] = value;

            if (checkChanges.is_edit_settings === this.editedPermissions[currentRoleKey].is_edit_settings &&
                checkChanges.is_upload_file === this.editedPermissions[currentRoleKey].is_upload_file &&
                checkChanges.is_read_all === this.editedPermissions[currentRoleKey].is_read_all &&
                checkChanges.is_locked_collection === this.editedPermissions[currentRoleKey].is_locked_collection &&
                checkChanges.is_create_folder === this.editedPermissions[currentRoleKey].is_create_folder &&
                checkChanges.is_edit_signer === this.editedPermissions[currentRoleKey].is_edit_signer &&
                checkChanges.is_delete === this.editedPermissions[currentRoleKey].is_delete &&
                checkChanges.is_read_approved_doc === this.editedPermissions[currentRoleKey].is_read_approved_doc &&
                checkChanges.is_edit_approver === this.editedPermissions[currentRoleKey].is_edit_approver &&
                checkChanges.is_edit_property === this.editedPermissions[currentRoleKey].is_edit_property
            ) {
                delete this.editedPermissions[currentRoleKey];

                if (!Object.keys(this.editedPermissions).length) {
                    this.isPermissionsChanged = false;
                    this.changeEditEvent.emit(this.isPermissionsChanged);
                }
            }
        }
    }

    deleteAccess(access: any): void {
        let alert: string;
        if (access.role_type === 'department') {
            alert = this.translate.instant('CONFIRM.TEXT.DELETE_DEPARTMENT_ACCESS', {name: access.role.name});
        } else if (access.role_type === 'member') {
            alert = this.translate.instant('CONFIRM.TEXT.DELETE_MEMBER_ACCESS', {name: this.helper.fullName(access.role)});
        } else {
            alert = this.translate.instant('CONFIRM.TEXT.DELETE_DIVISION_ACCESS', {name: access.role.name});
        }

        const modalSettings = {
            modalHeader: this.translate.instant('CONFIRM.HEADERS.DELETE'),
            modalBody: alert,
            buttonSubmit: this.translate.instant('CONFIRM.BUTTONS.YES'),
            buttonClose: this.translate.instant('CONFIRM.BUTTONS.NO')
        };
        const modal = this.modalService.createFromSettings(modalSettings);
        modal.result.then(data => {
            if (data) {
                const deletePermission = {
                    role_type: access.role_type,
                    role_key: access.role.id ? access.role.id : access.role.guid,
                };

                const rightKey = this.target.key ? this.target.key : (this.target as NavigationTableResponseItemModel).id;

                this.docApi.deleteDocAccess(
                    this.target.type_str,
                    rightKey,
                    deletePermission
                ).subscribe({
                    next: () => {
                        this.alert.setAlert(this.translate.instant('ALERTS.SUCCESS'), 'green');
                        this.getAccess();
                    }, error: err => {
                        this.alert.setAlert(err.message, 'blue');
                    }
                });
            }
        }).catch(() => {
        });
    }

    async savePermissions(): Promise<void> {
        await this.saveRequests().then(() => {
            this.alert.setAlert(this.translate.instant('ALERTS.SUCCESS'), 'green');
        }).catch(() => {
            this.alert.setAlert(this.translate.instant('ALERTS.ERROR'), 'blue');
        });

        this.isPermissionsChanged = false;
        this.changeEditEvent.emit(this.isPermissionsChanged);
        this.editedPermissions = {};
        this.getAccess();
    }

    async saveRequests(): Promise<void> {
        return new Promise((resolve, reject) => {
            let error = false;
            Object.keys(this.editedPermissions).forEach(key => {
                const data = this.editedPermissions[key];

                const rightKey = this.target.key ? this.target.key : (this.target as NavigationTableResponseItemModel).id;

                this.docApi.setDocAccess(this.target.type_str, rightKey, data).subscribe({
                    error: () => {
                        error = true;
                    }
                });
            });
            if (error) {
                reject();
            } else {
                resolve();
            }
        });
    }

    cancelEditPermissions(): void {
        this.editedPermissions = {};
        this.isPermissionsChanged = false;
        this.changeEditEvent.emit(this.isPermissionsChanged);
        this.members = structuredClone(this.copyPermissions.members);
        this.divisions = structuredClone(this.copyPermissions.divisions);
        this.departments = structuredClone(this.copyPermissions.departments);
    }
}
